import React, { useState } from 'react';
import { Instagram, ArrowUpIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwBcryCHbhl4goMnV2lwnapFXyFPEwUC8DwXhighI0dwnhuzU-Dz15r5sgLDqjHiuAEPg/exec';
export function Footer() {
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (status === 'submitting') return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const data = await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim(),
          date: new Date().toISOString(),
          source: 'website',
          secret: 'ALE_NEWSLETTER_2026_x9Kp2Lq8Mz7Rw41'
        })
      }).then(async res => {
        const json = await res.json();
        if (!json.ok) {
          throw new Error(json.error || 'Subscription failed');
        }
        return json;
      });
      setStatus('success');
      setEmail('');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <footer id="contact" className="bg-charcoal text-white relative" data-id="element-37">
      {/* Main Footer Content — 3 columns */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-38">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8" data-id="element-39">
          {/* Column 1: Newsletter */}
          <div className="text-center md:text-left" data-id="element-40">
            <h3 className="font-serif text-xl md:text-2xl tracking-[0.15em] uppercase mb-6" data-id="element-41">
              Join the Journey
            </h3>

            <AnimatePresence mode="wait" data-id="element-42">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.4
            }} className="text-center md:text-left" data-id="element-43">
                  <div className="flex items-center justify-center md:justify-start space-x-3 mb-4" data-id="element-44">
                    <CheckCircleIcon size={20} className="text-green-400" data-id="element-45" />
                    <span className="text-sm font-sans font-medium text-white" data-id="element-46">
                      Thanks for joining the newsletter.
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 font-sans leading-relaxed mb-6" data-id="element-47">
                    You'll be the first to know about new series, limited
                    editions, and exclusive content.
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-[10px] font-sans tracking-[0.15em] uppercase text-gray-500 hover:text-white transition-colors underline underline-offset-4" data-id="element-48">
                    Subscribe another email
                  </button>
                </motion.div> : <motion.div key="form" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.4
            }} data-id="element-49">
                  <p className="text-sm text-gray-400 font-sans leading-relaxed mb-8" data-id="element-50">
                    Become an insider to receive exclusive first access to new
                    series, limited editions, and more.
                  </p>
                  <form onSubmit={handleSubmit} className="flex" data-id="element-51">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ENTER YOUR EMAIL" className="flex-1 bg-white/10 border border-gray-700 text-white text-xs font-sans tracking-[0.15em] placeholder-gray-500 px-4 py-3 focus:outline-none focus:border-gray-500 transition-colors" required disabled={status === 'submitting'} data-id="element-52" />
                    <button type="submit" disabled={status === 'submitting'} className="bg-gray-700 hover:bg-gray-600 text-white text-xs font-sans tracking-[0.15em] uppercase px-6 py-3 transition-colors disabled:opacity-50" data-id="element-53">
                      {status === 'submitting' ? '...' : 'SUBMIT'}
                    </button>
                  </form>
                  {status === 'error' && <p className="text-xs text-red-400 font-sans mt-3" data-id="element-54">
                      {errorMsg || 'Something went wrong. Please try again.'}
                    </p>}
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Column 2: Contact */}
          <div className="text-center border-t md:border-t-0 md:border-l md:border-r border-gray-800 pt-12 md:pt-0 md:px-8" data-id="element-55">
            <h3 className="font-serif text-xl md:text-2xl tracking-[0.15em] uppercase mb-6" data-id="element-56">
              Contact Us
            </h3>
            <p className="text-sm text-gray-400 font-sans leading-relaxed mb-8" data-id="element-57">
              To make an inquiry, request a custom quote or just keep in touch,
              please contact us at:
            </p>
            <a href="mailto:info@alejandronoriega.art" className="text-sm font-sans text-white underline underline-offset-4 hover:text-gray-300 transition-colors" data-id="element-58">
              info@alejandronoriega.art
            </a>
          </div>

          {/* Column 3: Follow / Social */}
          <div className="text-center md:text-right border-t md:border-t-0 pt-12 md:pt-0" data-id="element-59">
            <h3 className="font-serif text-xl md:text-2xl tracking-[0.15em] uppercase mb-6" data-id="element-60">
              Follow Alejandro
            </h3>
            <div className="flex items-center justify-center md:justify-end space-x-6 mb-10" data-id="element-61">
              <a href="https://www.instagram.com/alenoriegaes" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-400 transition-colors" aria-label="Instagram" data-id="element-62">
                <Instagram size={22} data-id="element-63" />
              </a>
            </div>
            <p className="text-xs text-gray-500 font-sans leading-relaxed" data-id="element-64">
              Copyright {new Date().getFullYear()} by Alejandro Noriega
              Photography. All Rights Reserved.
            </p>
            <a href="/privacy" className="text-[10px] font-sans tracking-[0.12em] uppercase text-gray-600 hover:text-gray-400 transition-colors mt-3 inline-block" data-id="element-65">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar with mail icon and scroll-to-top */}
      <div className="border-t border-gray-800" data-id="element-66">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between" data-id="element-67">
          <a href="mailto:info@alejandronoriega.art" className="w-10 h-10 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-gray-500 transition-colors" aria-label="Email us" data-id="element-68">
            <MailIcon size={18} data-id="element-69" />
          </a>
          <button onClick={scrollToTop} className="w-10 h-10 border border-gray-700 flex items-center justify-center text-gray-400 hover:text-white hover:border-gray-500 transition-colors" aria-label="Scroll to top" data-id="element-70">
            <ArrowUpIcon size={18} data-id="element-71" />
          </button>
        </div>
      </div>
    </footer>;
}